/**
 * Client-Side Pose Extraction Hook
 * 
 * Runs MediaPipe pose extraction in the browser instead of server-side.
 * This is faster (~5-8s vs ~16s) and uses the user's GPU.
 * 
 * Flow:
 * 1. Load video into hidden <video> element
 * 2. Initialize MediaPipe PoseLandmarker
 * 3. Process frames as video plays
 * 4. Collect landmarks and save to swing_runs.pose_data
 * 5. Trigger AI analysis (skipping server pose extraction)
 */

import { useState, useCallback, useRef } from 'react';
import { PoseLandmarker, FilesetResolver, PoseLandmarkerResult } from '@mediapipe/tasks-vision';
import Hls from 'hls.js';
import { supabase } from '@/integrations/supabase/client';

export type PoseExtractionStatus = 
  | 'idle'
  | 'loading_video'
  | 'initializing_mediapipe'
  | 'extracting'
  | 'saving'
  | 'triggering_analysis'
  | 'complete'
  | 'error';

export interface PoseExtractionProgress {
  status: PoseExtractionStatus;
  progress: number; // 0-100
  framesProcessed: number;
  totalFrames: number;
  error?: string;
}

export interface ClientPoseExtractionResult {
  success: boolean;
  framesProcessed: number;
  durationMs: number;
  error?: string;
}

const TARGET_FPS = 30;
const MODEL_URL = 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task';

export function useClientPoseExtraction() {
  const [progress, setProgress] = useState<PoseExtractionProgress>({
    status: 'idle',
    progress: 0,
    framesProcessed: 0,
    totalFrames: 0,
  });

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const poseLandmarkerRef = useRef<PoseLandmarker | null>(null);
  const abortRef = useRef(false);

  /**
   * Initialize MediaPipe PoseLandmarker
   * 
   * IMPORTANT: forceNew=true creates a fresh instance to avoid timestamp
   * mismatch errors when processing multiple videos. MediaPipe's VIDEO mode
   * expects monotonically increasing timestamps; reusing an instance across
   * videos causes "Packet timestamp mismatch" errors.
   */
  const initMediaPipe = useCallback(async (forceNew = false): Promise<PoseLandmarker> => {
    // Close existing instance if forceNew requested (needed for each new video)
    if (forceNew && poseLandmarkerRef.current) {
      console.log('[ClientPose] Closing previous MediaPipe instance for new video');
      poseLandmarkerRef.current.close();
      poseLandmarkerRef.current = null;
    }

    if (poseLandmarkerRef.current) {
      return poseLandmarkerRef.current;
    }

    console.log('[ClientPose] Initializing MediaPipe...');
    const vision = await FilesetResolver.forVisionTasks(
      'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
    );

    const poseLandmarker = await PoseLandmarker.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: MODEL_URL,
        delegate: 'GPU',
      },
      runningMode: 'VIDEO',
      numPoses: 1,
      minPoseDetectionConfidence: 0.5,
      minPosePresenceConfidence: 0.5,
      minTrackingConfidence: 0.5,
      outputSegmentationMasks: false,
    });

    poseLandmarkerRef.current = poseLandmarker;
    console.log('[ClientPose] MediaPipe initialized');
    return poseLandmarker;
  }, []);

  /**
   * Extract pose data from a Cloudflare video URL
   * 
   * @param cfStreamUid - Cloudflare Stream UID
   * @param runId - swing_runs.id
   * @param options - Optional configuration
   * @param options.onProgress - Progress callback
   * @param options.demoSessionId - If set, use save_demo_pose_and_analyze RPC
   *   to bypass RLS (demo runs are owned by the prospect, not the host).
   *   The RPC atomically saves pose data and enqueues analysis.
   */
  const extractPose = useCallback(async (
    cfStreamUid: string,
    runId: string,
    options?: {
      onProgress?: (p: PoseExtractionProgress) => void;
      demoSessionId?: string;
    }
  ): Promise<ClientPoseExtractionResult> => {
    const onProgress = options?.onProgress;
    const demoSessionId = options?.demoSessionId;
    const startTime = Date.now();
    abortRef.current = false;

    const updateProgress = (update: Partial<PoseExtractionProgress>) => {
      setProgress(prev => {
        const next = { ...prev, ...update };
        onProgress?.(next);
        return next;
      });
    };

    let hlsInstance: Hls | null = null;
    try {
      // Step 1: Create video element and load video
      updateProgress({ status: 'loading_video', progress: 5 });
      
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.playsInline = true;
      videoRef.current = video;

      const mp4Url = `https://videodelivery.net/${cfStreamUid}/downloads/default.mp4`;
      const hlsUrl = `https://customer-uh5f9h1v1dpl1ux6.cloudflarestream.com/${cfStreamUid}/manifest/video.m3u8`;
      const LOAD_TIMEOUT_MS = 30000;

      const loadVideo = (): Promise<void> =>
        new Promise((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('Video load timeout')), LOAD_TIMEOUT_MS);
          video.onloadedmetadata = () => {
            clearTimeout(timeout);
            resolve();
          };
          video.onerror = () => {
            clearTimeout(timeout);
            reject(new Error(`Video load error: ${video.error?.message || 'Unknown'}`));
          };
        });

      // Try to load video with retries — Cloudflare may still be transcoding
      const MAX_LOAD_RETRIES = 10;
      const LOAD_RETRY_DELAY_MS = 3000;

      for (let loadAttempt = 0; ; loadAttempt++) {
        if (abortRef.current) {
          return { success: false, framesProcessed: 0, durationMs: Date.now() - startTime, error: 'Aborted' };
        }

        try {
          try {
            console.log('[ClientPose] Loading video (MP4):', mp4Url);
            video.src = mp4Url;
            await loadVideo();
            break;
          } catch (mp4Err) {
            const msg = mp4Err instanceof Error ? mp4Err.message : String(mp4Err);
            const isMediaError = msg.includes('Format error') || msg.includes('MEDIA_ELEMENT_ERROR');
            if (!isMediaError) throw mp4Err;

            console.log('[ClientPose] MP4 failed, trying HLS fallback:', hlsUrl);
            video.src = '';
            video.removeAttribute('src');

            if (Hls.isSupported()) {
              await new Promise<void>((resolve, reject) => {
                const timeout = setTimeout(() => reject(new Error('HLS load timeout')), LOAD_TIMEOUT_MS);
                hlsInstance = new Hls({ enableWorker: true });
                hlsInstance.loadSource(hlsUrl);
                hlsInstance.attachMedia(video);
                hlsInstance.on(Hls.Events.MANIFEST_PARSED, () => { clearTimeout(timeout); });
                hlsInstance.on(Hls.Events.ERROR, (_e, data) => {
                  if (data.fatal) {
                    clearTimeout(timeout);
                    reject(new Error(`HLS error: ${data.type} ${data.details}`));
                  }
                });
                video.onloadedmetadata = () => {
                  clearTimeout(timeout);
                  resolve();
                };
                video.onerror = () => {
                  clearTimeout(timeout);
                  reject(new Error(`Video load error: ${video.error?.message || 'Unknown'}`));
                };
              });
            } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
              video.src = hlsUrl;
              await loadVideo();
            } else {
              throw new Error('MP4 failed and HLS is not supported in this browser. Try a different browser or video.');
            }
          }
          break;
        } catch (loadErr) {
          const errMsg = loadErr instanceof Error ? loadErr.message : String(loadErr);
          const isRetryable = errMsg.includes('manifestLoadError') ||
                              errMsg.includes('networkError') ||
                              errMsg.includes('HLS load timeout') ||
                              errMsg.includes('Video load timeout');

          if (!isRetryable || loadAttempt >= MAX_LOAD_RETRIES) {
            throw loadErr;
          }

          console.log(`[ClientPose] Video not ready (Cloudflare transcoding), retry ${loadAttempt + 1}/${MAX_LOAD_RETRIES} in ${LOAD_RETRY_DELAY_MS / 1000}s...`);

          if (hlsInstance) {
            hlsInstance.destroy();
            hlsInstance = null;
          }
          video.src = '';
          video.removeAttribute('src');
          video.load();

          updateProgress({ status: 'loading_video', progress: 5 });
          await new Promise(r => setTimeout(r, LOAD_RETRY_DELAY_MS));
        }
      }

      const duration = video.duration;
      const totalFrames = Math.ceil(duration * TARGET_FPS);
      console.log('[ClientPose] Video loaded:', { duration, totalFrames });

      updateProgress({ status: 'initializing_mediapipe', progress: 10, totalFrames });

      // Step 2: Initialize MediaPipe (forceNew=true to avoid timestamp mismatch on multiple uploads)
      const poseLandmarker = await initMediaPipe(true);

      if (abortRef.current) {
        return { success: false, framesProcessed: 0, durationMs: Date.now() - startTime, error: 'Aborted' };
      }

      // Step 3: Process frames
      updateProgress({ status: 'extracting', progress: 15 });

      const landmarks: Array<{
        timestamp: number;
        landmarks: Array<{ x: number; y: number; z: number; visibility?: number }>;
        worldLandmarks?: Array<{ x: number; y: number; z: number }>;
      }> = [];

      const frameInterval = 1 / TARGET_FPS;
      let currentTime = 0;
      let framesProcessed = 0;

      // Seek through video and extract poses
      while (currentTime < duration && !abortRef.current) {
        video.currentTime = currentTime;
        
        await new Promise<void>((resolve) => {
          video.onseeked = () => resolve();
        });

        // Run pose detection
        const timestampMs = currentTime * 1000;
        const result: PoseLandmarkerResult = poseLandmarker.detectForVideo(video, timestampMs);

        if (result.landmarks && result.landmarks.length > 0) {
          const frameLandmarks = result.landmarks[0].map((lm, idx) => ({
            x: lm.x,
            y: lm.y,
            z: lm.z,
            visibility: lm.visibility,
          }));

          const frameWorldLandmarks = result.worldLandmarks?.[0]?.map(lm => ({
            x: lm.x,
            y: lm.y,
            z: lm.z,
          }));

          landmarks.push({
            timestamp: currentTime,
            landmarks: frameLandmarks,
            worldLandmarks: frameWorldLandmarks,
          });
        }

        framesProcessed++;
        currentTime += frameInterval;

        // Update progress (15-80% range for extraction)
        const extractionProgress = 15 + (framesProcessed / totalFrames) * 65;
        updateProgress({
          progress: Math.min(80, extractionProgress),
          framesProcessed,
        });
      }

      console.log('[ClientPose] Extraction complete:', { framesProcessed, landmarkFrames: landmarks.length });

      if (abortRef.current) {
        return { success: false, framesProcessed, durationMs: Date.now() - startTime, error: 'Aborted' };
      }

      // Step 4: Save pose data + trigger analysis
      updateProgress({ status: 'saving', progress: 85 });

      const poseData = {
        frames: landmarks.length,
        fps: TARGET_FPS,
        landmarks: landmarks,
        meta: {
          extractionMethod: 'client',
          durationSec: duration,
          processingTimeMs: Date.now() - startTime,
          modelVersion: 'pose_landmarker_lite',
        },
      };

      if (demoSessionId) {
        // Demo mode: use SECURITY DEFINER RPC to bypass RLS.
        // The host's auth can't directly update the prospect's swing_run.
        // This RPC atomically saves pose data AND enqueues the analyze job.
        updateProgress({ status: 'triggering_analysis', progress: 90 });
        const { error: rpcError } = await supabase.rpc('save_demo_pose_and_analyze', {
          p_run_id: runId,
          p_pose_data: poseData.landmarks,
          p_demo_session_id: demoSessionId,
        });

        if (rpcError) {
          throw new Error(`Failed to save demo pose data: ${rpcError.message}`);
        }

        console.log('[ClientPose] Demo pose data saved and analysis enqueued via RPC');
      } else {
        // V3 mode: direct update (user owns their own run)
        const { error: updateError } = await supabase
          .from('swing_runs')
          .update({
            pose_data: poseData.landmarks,
            pipeline_state: 'pose_complete',
            status: 'processing',
            analysis_stage: 'telemetry',
            analysis_progress: 65,
            updated_at: new Date().toISOString(),
          })
          .eq('id', runId);

        if (updateError) {
          throw new Error(`Failed to save pose data: ${updateError.message}`);
        }

        console.log('[ClientPose] Pose data saved to database');

        updateProgress({ status: 'triggering_analysis', progress: 90 });
        await triggerAnalysisOnly(runId);
      }

      updateProgress({ status: 'complete', progress: 100 });

      const durationMs = Date.now() - startTime;
      console.log('[ClientPose] Complete:', { framesProcessed, durationMs });

      return {
        success: true,
        framesProcessed,
        durationMs,
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('[ClientPose] Error:', errorMessage);
      
      updateProgress({ status: 'error', error: errorMessage });

      return {
        success: false,
        framesProcessed: progress.framesProcessed,
        durationMs: Date.now() - startTime,
        error: errorMessage,
      };
    } finally {
      if (hlsInstance) {
        hlsInstance.destroy();
        hlsInstance = null;
      }
      if (videoRef.current) {
        videoRef.current.src = '';
        videoRef.current = null;
      }
    }
  }, [initMediaPipe, progress.framesProcessed]);

  /**
   * Abort ongoing extraction
   */
  const abort = useCallback(() => {
    abortRef.current = true;
  }, []);

  /**
   * Cleanup MediaPipe resources
   */
  const cleanup = useCallback(() => {
    if (poseLandmarkerRef.current) {
      poseLandmarkerRef.current.close();
      poseLandmarkerRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.src = '';
      videoRef.current = null;
    }
    setProgress({
      status: 'idle',
      progress: 0,
      framesProcessed: 0,
      totalFrames: 0,
    });
  }, []);

  return {
    extractPose,
    progress,
    abort,
    cleanup,
  };
}

/**
 * Trigger only the AI analysis step (skip pose extraction).
 * Uses RPC enqueue_analyze_job (SECURITY DEFINER) so the server inserts into
 * job_queue; direct client insert is blocked by RLS.
 */
async function triggerAnalysisOnly(runId: string): Promise<void> {
  console.log('[ClientPose] Triggering AI analysis for run:', runId);

  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('User not authenticated');
  }

  const { error } = await supabase.rpc('enqueue_analyze_job', { p_run_id: runId });

  if (error) {
    if (error.message?.includes('already') || error.code === '23505') {
      console.log('[ClientPose] Analysis job already queued, continuing');
      return;
    }
    throw new Error(`Failed to queue analysis: ${error.message}`);
  }

  console.log('[ClientPose] Analysis job queued successfully');
}
